/**
 * Storage diagnostics and health check functions
 */

import { categorizeKey, isReduxPersistKey } from './categories';
import { type StorageHealth, type StorageIssue, type StorageSnapshot } from './types';

/**
 * Check if a recording is currently active
 * Used to disable storage clearing during active recordings
 */
export function isRecordingActive(): boolean {
  try {
    const recordingStatus = localStorage.getItem('recordingStatus');
    if (!recordingStatus) return false;

    const status = JSON.parse(recordingStatus) as Record<string, unknown>;
    return status?.isRecording === true || status?.recordingPaused === true;
  } catch (error) {
    console.warn('Failed to check recording status:', error);
    return false;
  }
}

/**
 * Get estimated size of audio cache in IndexedDB
 */
export async function getAudioCacheSize(): Promise<number> {
  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    }
    return 0;
  } catch (error) {
    console.warn('Failed to get audio cache size:', error);
    return 0;
  }
}

/**
 * Get size of a localStorage item in bytes
 */
function getLocalStorageItemSize(key: string): number {
  try {
    const value = localStorage.getItem(key);
    if (!value) return 0;
    return new Blob([value]).size;
  } catch (error) {
    return 0;
  }
}

/**
 * Get total size of localStorage in bytes
 */
export function getLocalStorageSize(): number {
  try {
    const keys = Object.keys(localStorage);
    return keys.reduce((total, key) => total + getLocalStorageItemSize(key), 0);
  } catch (error) {
    console.warn('Failed to calculate localStorage size:', error);
    return 0;
  }
}

/**
 * Get a snapshot of current storage state
 * Useful for debugging and diagnostics
 */
export async function getStorageSnapshot(): Promise<StorageSnapshot> {
  try {
    const allKeys = Object.keys(localStorage);
    const keysByCategory: Record<string, string[]> = {};

    // Categorize all keys
    allKeys.forEach(key => {
      const category = categorizeKey(key, 'localStorage');
      if (!keysByCategory[category]) {
        keysByCategory[category] = [];
      }
      keysByCategory[category].push(key);
    });

    // Get Redux persist keys and size
    const reduxPersistKeys = allKeys.filter(key => isReduxPersistKey(key));
    const reduxPersistSize = reduxPersistKeys.reduce(
      (total, key) => total + getLocalStorageItemSize(key),
      0,
    );

    // Get IndexedDB info
    const indexedDBInfo: StorageSnapshot['indexedDB'] = {
      available: false,
    };

    try {
      if ('indexedDB' in window) {
        indexedDBInfo.available = true;

        if ('storage' in navigator && 'estimate' in navigator.storage) {
          const estimate = await navigator.storage.estimate();
          indexedDBInfo.usage = estimate.usage;
          indexedDBInfo.quota = estimate.quota;
        }

        // Try to get database names (may not be supported in all browsers)
        if ('databases' in indexedDB) {
          const databases = await indexedDB.databases();
          indexedDBInfo.databases = databases.map(db => db.name || 'unknown');
        }
      }
    } catch (error) {
      console.warn('Failed to get IndexedDB info:', error);
    }

    // Get sessionStorage info
    const sessionKeys = Object.keys(sessionStorage);
    const sessionSize = sessionKeys.reduce(
      (total, key) => total + new Blob([sessionStorage.getItem(key) || '']).size,
      0,
    );

    return {
      localStorage: {
        totalKeys: allKeys.length,
        totalSize: getLocalStorageSize(),
        keysByCategory,
      },
      sessionStorage: {
        totalKeys: sessionKeys.length,
        totalSize: sessionSize,
      },
      indexedDB: indexedDBInfo,
      reduxPersist: {
        keys: reduxPersistKeys,
        totalSize: reduxPersistSize,
      },
    };
  } catch (error) {
    console.error('Failed to get storage snapshot:', error);
    // Return minimal snapshot on error
    return {
      localStorage: {
        totalKeys: 0,
        totalSize: 0,
        keysByCategory: {},
      },
      sessionStorage: {
        totalKeys: 0,
        totalSize: 0,
      },
      indexedDB: {
        available: false,
      },
      reduxPersist: {
        keys: [],
        totalSize: 0,
      },
    };
  }
}

/**
 * Analyze storage health and detect potential issues
 */
export async function getStorageHealth(): Promise<StorageHealth> {
  const issues: StorageIssue[] = [];
  let totalUsage = 0;
  let totalQuota = 0;

  try {
    // Check storage quota
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      totalUsage = estimate.usage || 0;
      totalQuota = estimate.quota || 0;

      const usagePercent = totalQuota > 0 ? (totalUsage / totalQuota) * 100 : 0;

      // Issue: High storage usage
      if (usagePercent > 90) {
        issues.push({
          type: 'quota_high',
          severity: 'error',
          description: `Storage is ${usagePercent.toFixed(1)}% full. This may cause recording failures.`,
          suggestedFix: 'clearCache',
        });
      } else if (usagePercent > 75) {
        issues.push({
          type: 'quota_high',
          severity: 'warning',
          description: `Storage is ${usagePercent.toFixed(1)}% full. Consider clearing old recordings.`,
          suggestedFix: 'clearCache',
        });
      }
    }

    // Check for stale encounter state
    const activeEncounterId = localStorage.getItem('activeEncounterId');
    if (activeEncounterId) {
      // We could check if this encounter actually exists, but that requires an API call
      // For now, just note that there is an active encounter ID set
      issues.push({
        type: 'stale_encounter',
        severity: 'info',
        description: 'An encounter is currently set as active.',
        suggestedFix: 'clearEncounter',
      });
    }

    // Check for orphaned recording sessions
    const currentRecordingSessionId = localStorage.getItem('currentRecordingSessionId');
    const recordingStatus = localStorage.getItem('recordingStatus');
    if (currentRecordingSessionId && !recordingStatus) {
      issues.push({
        type: 'orphaned_recording',
        severity: 'warning',
        description: 'Found recording session ID without active recording status.',
        suggestedFix: 'clearRecording',
      });
    }

    // Check if IndexedDB is available
    if (!('indexedDB' in window)) {
      issues.push({
        type: 'indexeddb_unavailable',
        severity: 'error',
        description: 'IndexedDB is not available. Recording features may not work.',
        suggestedFix: 'clearAll',
      });
    }
  } catch (error) {
    console.error('Failed to analyze storage health:', error);
  }

  const usagePercent = totalQuota > 0 ? (totalUsage / totalQuota) * 100 : 0;

  return {
    hasIssues: issues.length > 0,
    issues,
    totalUsage,
    totalQuota,
    usagePercent,
  };
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
